/**
 * ReportTemplateItem Component
 *
 * Individual draggable report template item with ordering and exclusion controls
 */

import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import type { ReportTemplateItemProps } from './types'

const ReportTemplateItem: React.FC<ReportTemplateItemProps> = ({
  template,
  setting,
  index,
  onToggleIgnored,
  disabled = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: template.key,
    disabled
  })

  const isIgnored = setting.ignored

  const combinedStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isIgnored ? designTokens.colors.gray[0] : designTokens.colors.white,
    borderColor: designTokens.colors.gray[1]
  }

  return (
    <div
      ref={setNodeRef}
      style={combinedStyle}
      className="flex items-center gap-3 p-3 rounded border"
      data-ignored={isIgnored}
    >
      {/* Order Number */}
      <div
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded"
        style={{
          backgroundColor: isIgnored
            ? designTokens.colors.gray[1]
            : designTokens.colors.primary.light,
          color: isIgnored ? designTokens.colors.gray[4] : designTokens.colors.white
        }}
      >
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-small'][0],
            fontWeight: 600
          }}
        >
          {index + 1}
        </span>
      </div>

      {/* Drag Handle */}
      <button
        type="button"
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label="드래그하여 순서 변경"
        style={{
          opacity: disabled ? 0.3 : 1,
          cursor: disabled ? 'not-allowed' : 'grab'
        }}
      >
        <GripVertical
          size={20}
          style={{
            color: isIgnored ? designTokens.colors.gray[3] : designTokens.colors.gray[5]
          }}
        />
      </button>

      {/* Template Name */}
      <div className="flex-1 min-w-0">
        <span
          className="block truncate"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            fontWeight: 400,
            color: isIgnored ? designTokens.colors.gray[4] : designTokens.colors.gray[6]
          }}
        >
          {template.name}
        </span>
        {template.description && (
          <span
            className="block truncate mt-1"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              fontWeight: 400,
              color: designTokens.colors.gray[4]
            }}
          >
            {template.description}
          </span>
        )}
      </div>

      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => onToggleIgnored(template.key)}
        disabled={disabled}
        className="flex-shrink-0 px-4 py-2 rounded transition-colors"
        style={{
          backgroundColor: isIgnored
            ? designTokens.colors.gray[1]
            : designTokens.colors.primary.DEFAULT,
          color: isIgnored ? designTokens.colors.gray[5] : designTokens.colors.white,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-small'][0],
          fontWeight: 600,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: 'none'
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.opacity = '0.9'
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.opacity = '1'
          }
        }}
        aria-label={isIgnored ? '보고서 포함' : '보고서 제외'}
      >
        {isIgnored ? '제외됨' : '포함'}
      </button>
    </div>
  )
}

export default ReportTemplateItem
